import React, { useEffect, useMemo, useState } from 'react'
import AdminHeader from 'live/components/header/ui/admin/header'
import globalNotification from 'live/services/global-notification'
import styled from 'styled-components'
import { AnswerType, npsApi } from '../../api/npsApi'
import globalConfirmation from 'live/services/global-confirmation'
import SubmitOrCancel from 'live/components/forms/ui/submit-or-cancel'
import Select from 'react-select'
import i18n from 'live/services/i18n'

interface AdminProps {}

const Admin: React.FC<AdminProps> = () => {
    const [answers, setAnswers] = useState<AnswerType[]>([])
    const [liveUsers, setLiveUsers] = useState<{ id: string; username: string; email: string }[]>([])
    const [isAddingUser, setIsAddingUser] = useState(false)
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null)

    const loadAnswers = () => {
        npsApi.getAnswers().then(res => {
            if (res?.success) {
                setAnswers(res?.data || [])
                return
            }

            globalNotification.error(i18n('Could not load NPS answers'))
        })
    }

    useEffect(() => {
        loadAnswers()

        fetch('/rest/user', {
            method: 'GET',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' }
        })
            .then(response => response.json())
            .then(data => {
                setLiveUsers(data?.data || [])
            })
            .catch(() => {
                globalNotification.error(i18n('Could not load platform users'))
            })
    }, [])

    const handleDeleteUser = (userId: number) => {
        globalConfirmation.confirm(
            <div>
                <p>{i18n('Are you sure you want to delete?')}</p>
            </div>,
            () => {
                npsApi
                    .deleteUserAnswer(userId)
                    .then(res => {
                        if (res.success) {
                            globalNotification.success(i18n('NPS answer deleted successfully'))
                        } else {
                            globalNotification.error(i18n('Error deleting NPS answer'))
                        }
                    })
                    .finally(loadAnswers)
            }
        )
    }

    const handleRegisterUser = () => {
        if (selectedUserId == null) {
            globalNotification.error(i18n('Please select a user'))
            return
        }

        if (answers.some(answer => answer.userId === selectedUserId)) {
            globalNotification.error(i18n('This user is already registered'))
            return
        }

        npsApi
            .registerUser(selectedUserId)
            .then(res => {
                if (res?.success) {
                    globalNotification.success(i18n('User registered successfully'))
                    setIsAddingUser(false)
                    setSelectedUserId(null)
                    loadAnswers()
                    return
                }

                globalNotification.error(res?.error || i18n('Could not register user'))
            })
            .catch(() => {
                globalNotification.error(i18n('Could not register user'))
            })
    }

    return useMemo(() => {
        const selectableUsers = liveUsers.filter(user => !answers.some(answer => answer.userId === Number(user.id)))
        const selectedUser = selectableUsers.find(user => Number(user.id) === selectedUserId)
        console.log(selectedUser, selectableUsers, answers);
        
        return (
            <Container className="admin-content">
                <AdminHeader>
                    <h2>{i18n('NPS Survey')}</h2>
                </AdminHeader>
                <Body>
                    <HeaderSection>
                        <AddButton type="button" onClick={() => setIsAddingUser(true)}>
                            {i18n('Add User')}
                        </AddButton>
                    </HeaderSection>

                    {isAddingUser && (
                        <AddUserForm>
                            <FormTitle>{i18n('Add User to NPS survey')}</FormTitle>
                            <FormRow>
                                <FormGroup>
                                    <Label>{i18n('User')}</Label>
                                    <Select
                                        value={
                                            selectedUser
                                                ? {
                                                      value: Number(selectedUser.id),
                                                      label: `${selectedUser.username} (${selectedUser.email})`
                                                  }
                                                : null
                                        }
                                        onChange={option => setSelectedUserId(option?.value ?? null)}
                                        options={selectableUsers.map(user => ({
                                            value: Number(user.id),
                                            label: `${user.username} (${user.email})`
                                        }))}
                                        isDisabled={selectableUsers.length === 0}
                                        placeholder={i18n('Select user')}
                                    />
                                </FormGroup>
                            </FormRow>
                            {selectableUsers.length === 0 && (
                                <HelperText>{i18n('All available users are already registered')}</HelperText>
                            )}
                            <FormActions>
                                <SubmitOrCancel
                                    onSubmit={handleRegisterUser}
                                    onCancel={() => {
                                        setIsAddingUser(false)
                                        setSelectedUserId(null)
                                    }}
                                />
                            </FormActions>
                        </AddUserForm>
                    )}

                    <UserList>
                        <ListHeader>
                            <HeaderCell>{i18n('User')}</HeaderCell>
                            <HeaderCell>{i18n('Score')}</HeaderCell>
                            <HeaderCell>{i18n('Comment')}</HeaderCell>
                            <HeaderCell>{i18n('Answered')}</HeaderCell>
                            <HeaderCell>{i18n('Answered At')}</HeaderCell>
                            <HeaderCell>{i18n('Retries')}</HeaderCell>
                            <HeaderCell>{i18n('Last Retry')}</HeaderCell>
                            <HeaderCell>{i18n('Actions')}</HeaderCell>
                        </ListHeader>
                        {answers.length === 0 ? (
                            <EmptyState>
                                <EmptyMessage>{i18n('No NPS answers yet')}</EmptyMessage>
                                <EmptySubMessage>
                                    {i18n('Responses submitted in the popup will appear here.')}
                                </EmptySubMessage>
                            </EmptyState>
                        ) : (
                            answers.map(answer => {
                                const liveUser = liveUsers.find(user => Number(user.id) === answer.userId)

                                return (
                                    <UserRow key={answer.userId}>
                                        <UserCell>
                                            {liveUser
                                                ? `${liveUser.username} (${liveUser.email})`
                                                : answer.name || answer.userId || '-'}
                                        </UserCell>
                                        <UserCell>{answer.score ?? '-'}</UserCell>
                                        <UserCell>{answer.comment || '-'}</UserCell>
                                        <UserCell>{answer.answered ? i18n('Yes') : i18n('No')}</UserCell>
                                        <UserCell>
                                            {answer.answeredAt ? new Date(answer.answeredAt).toLocaleString() : '-'}
                                        </UserCell>
                                        <UserCell>{answer.numberOfRetries ?? '-'}</UserCell>
                                        <UserCell>
                                            {answer.lastRetryAt ? new Date(answer.lastRetryAt).toLocaleString() : '-'}
                                        </UserCell>
                                        <UserCell>
                                            <ActionButton onClick={() => answer.userId != null && handleDeleteUser(answer.userId)} delete>
                                                {i18n('Delete')}
                                            </ActionButton>
                                        </UserCell>
                                    </UserRow>
                                )
                            })
                        )}
                    </UserList>
                </Body>
            </Container>
        )
    }, [answers, isAddingUser, liveUsers, selectedUserId])
}

export default Admin

const Container = styled.div`
    display: flex;
    flex-direction: column;
`

const Body = styled.div`
    display: flex;
    margin-top: 55px;
    min-height: calc(100% - 55px);
    padding: 35px 28px;
    flex-direction: column;
`

const HeaderSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
`

const Title = styled.h3`
    margin: 0;
    color: var(--gray-700);
    font-size: 18px;
    font-weight: 600;
`

const AddButton = styled.button`
    background-color: var(--blue-500);
    color: white;
    border: none;
    border-radius: 4px;
    padding: 8px 16px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
`

const AddUserForm = styled.div`
    background-color: var(--gray-100);
    border: 1px solid var(--gray-300);
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 24px;
`

const FormTitle = styled.h4`
    margin: 0 0 16px 0 !important;
    color: var(--gray-700);
    font-size: 16px;
    font-weight: 600;
`

const FormRow = styled.div`
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
`

const FormGroup = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
`

const Label = styled.label`
    font-size: 14px;
    font-weight: 500;
    color: var(--gray-700);
    margin-bottom: 4px;
`

const FormActions = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-start;
    margin-top: 16px;
`

const HelperText = styled.div`
    font-size: 13px;
    color: var(--gray-600);
`

const UserList = styled.div`
    background-color: white;
    border: 1px solid var(--gray-300);
    border-radius: 8px;
    overflow: hidden;
`

const ListHeader = styled.div`
    display: grid;
    grid-template-columns: 2fr 0.7fr 2fr 0.8fr 1.2fr 0.8fr 1.2fr 0.8fr;
    background-color: var(--gray-100);
    border-bottom: 1px solid var(--gray-300);
    padding: 12px 16px;
    font-weight: 600;
    color: var(--gray-700);
    font-size: 14px;
`

const HeaderCell = styled.div`
    padding: 0 8px;
`

const UserRow = styled.div`
    display: grid;
    grid-template-columns: 2fr 0.7fr 2fr 0.8fr 1.2fr 0.8fr 1.2fr 0.8fr;
    background-color: var(--gray-100);
    border-bottom: 1px solid var(--gray-300);
    padding: 12px 16px;
    transition: background-color 0.2s;

    &:hover {
        background-color: var(--gray-300);
    }

    &:last-child {
        border-bottom: none;
    }
`

const UserCell = styled.div`
    padding: 0 8px;
    display: flex;
    align-items: center;
    font-size: 14px;
    gap: 8px;
    word-break: break-word;
`

const ActionButton = styled.button<{ delete?: boolean }>`
    background-color: ${props => (props.delete ? 'var(--red-500)' : 'var(--gray-500)')};
    color: white;
    border: none;
    border-radius: 4px;
    padding: 4px 8px;
    cursor: pointer;
    font-size: 12px;
    transition: background-color 0.2s;
`

const EmptyState = styled.div`
    padding: 40px 20px;
    text-align: center;
    background-color: var(--gray-100);
    color: var(--gray-600);
`

const EmptyMessage = styled.div`
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 8px;
`

const EmptySubMessage = styled.div`
    font-size: 14px;
    color: var(--gray-500);
`
